/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AfterViewInit, Component, HostListener, Input } from '@angular/core';
import { BaseComponent } from '../../base.component';
import { ErrorResponseModel, MenuConfigModel, MenuItemsModel } from '../../../models';
import { LogService, MenuService, NavigationService } from '../../../services';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-menu-3',
    templateUrl: './menu3.0.component.html',
    styleUrls: ['./menu3.0.component.scss']
})
export class Menu30Component extends BaseComponent implements AfterViewInit {
    /**
     * Menu object
     */
    @Input() public menuConfig: MenuConfigModel = <MenuConfigModel>{
        menuItems: []
    };

    /**
     * Sets the menu, so it hovers over the page content instead push the page right.
     */
    @Input() set secondMenuFloats(state: boolean) {
        this.menuService.menuTypeIsHover.next(state);
        this._secondMenuFloats = state;
    }

    /**
     * Enable user or another component to force the level menu open.
     */
    @Input() public forceLevelMenuOpen: boolean = false;

    /**
     * Store the menu state.
     */
    public isOpen: boolean = true;

    /**
     * Store the second level menu items for looping in the view.
     */
    public level2MenuItems: MenuItemsModel[] = [];

    /**
     * Second menu floats
     * @private
     */
    private _secondMenuFloats: boolean = false;

    /**
     * Menu opened manually by the user.
     * @private
     */
    private menuOpenedByUser: boolean = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private logService: LogService,
        private menuService: MenuService,
        private navigationService: NavigationService,
        private router: Router
    ) {
        super();

        this.setUpServiceSubscribers();
    }

    get secondMenuFloats() {
        return this._secondMenuFloats;
    }

    public ngAfterViewInit(): void {
        this.setUpRoutesSubscriber();
        this.setUpLevel2MenuForView();

        const path: string = this.navigationService.cleanEnduranceUrl(this.router.url);

        if (this.pathHasChildren('.' + path, this.menuConfig?.menuItems)) {
            this.menuStateHelper();
        }

        this.setItemState('.' + path);

        if (this.secondMenuFloats) {
            this.menuStateHelper();
            this.hideMenuItems(this.level2MenuItems);
        }
    }

    /**
     * Toggle menu state.
     */
    public toggleMenu(): void {
        if (!this.menuOpenedByUser) {
            this.menuOpenedByUser = true;
        }
        this.isOpen = !this.isOpen;
        this.menuService.menuIsOpen.next(this.isOpen);
    }

    /**
     * Toggle the menu chevron.
     * @param menuItem
     */
    public toggleChevron(menuItem: MenuItemsModel): 'chevron-right' | 'chevron-down' | 'none' {
        if (!menuItem.isChild) {
            return;
        }

        return menuItem.isChild && menuItem.active ? 'chevron-down' : 'chevron-right';
    }

    /**
     * Mouse over menu item.
     * @param menuItem
     */
    public mouseEnter(menuItem: MenuItemsModel): void {
        if (this.secondMenuFloats) {
            const timerId: any = setTimeout((): void => { // UX improvement
                this.hideMenuItems(this.level2MenuItems);
                menuItem.visible = true;
                clearTimeout(timerId);
            }, 150);
        }
    }

    /**
     * Capture the mouse leaving any part of the menu.
     */
    @HostListener('mouseleave')
    onMouseLeave(): void {
        if (this.secondMenuFloats) {
            const timerId: any = setTimeout((): void => {
                this.hideMenuItems(this.level2MenuItems);
                clearInterval(timerId);
            }, 250);
        }
    }

    /**
     * Hide visible menu items.
     * @param menuItems
     * @private
     */
    private hideMenuItems(menuItems: MenuItemsModel[]): void {
        const activeIndex: number = menuItems.findIndex((item: any): boolean => item.visible);
        if (activeIndex !== -1) {
            this.level2MenuItems[activeIndex].visible = false;
        }
    }

    /**
     * Toggle group and navigate
     * @param menuItem
     */
    public toggleNav(menuItem: any): void {
        if (menuItem.disabled || menuItem.comingSoon) {
            return;
        }

        if (menuItem.externalLink) {
            window.open(menuItem.location, '_blank');
            this.isOpen = false;

            return;
        }

        // Toggle level 3 parent and children.
        if (menuItem.isChild && menuItem.children?.length) {
            menuItem.active = !menuItem.active;

            return;
        }

        if (menuItem.children?.length) {
            if (!this.secondMenuFloats) {
                this.menuStateHelper();
                this.hideMenuItems(this.level2MenuItems);
                menuItem.visible = true;
            }

            return;
        }

        if (menuItem.location) {
            this.router.navigate([menuItem.location], { relativeTo: this.activatedRoute })
                .then((): void => {
                    if (!this.secondMenuFloats) {
                        this.checkIfRouteHasChildren();
                    }
                }).catch();
        }
    }

    /**
     * Menu state helper.
     * @private
     */
    private menuStateHelper(): void {
        if (!this.menuOpenedByUser) {
            this.isOpen = false;
            this.menuService.menuIsOpen.next(false);
        }
    }

    /**
     * Open the menu if the route is overview.
     * @private
     */
    private checkIfRouteHasChildren(): void {
        const path: string = '.' + this.navigationService.cleanEnduranceUrl(this.router.url);
        const hasChildren: boolean = this.pathHasChildren(path, this.menuConfig?.menuItems);

        if (!hasChildren) {
            this.isOpen = true;
            this.menuService.menuIsOpen.next(this.isOpen);
            this.hideMenuItems(this.level2MenuItems);
        }
    }

    /**
     * Path has children.
     * @param path
     * @param menuItems
     * @private
     */
    private pathHasChildren(path: string, menuItems: any[]): boolean {
        return menuItems.some((item: any) =>
            (path.includes(item?.location) || item?.location?.includes(path)) && item.children?.length
        );
    }

    /**
     * Subscribe to menu service to allow components to open and close the menu.
     * @private
     */
    private setUpServiceSubscribers(): void {
        this.menuService.menuIsOpen
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.hideMenuItems(this.level2MenuItems);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'Menu30Component: setUpServiceSubscribers',
                        'Failed to subscribe to menu service ' + error
                    );
                }
            });
    }

    /**
     * Subscribe to router events.
     * @private
     */
    private setUpRoutesSubscriber(): void {
        this.router.events
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any): void => {
                    if (data instanceof NavigationEnd) {
                        const path: string = this.navigationService.cleanEnduranceUrl(data.url);
                        this.setItemState('.' + path);

                        if (this.secondMenuFloats) {
                            this.hideMenuItems(this.level2MenuItems);
                        }
                    }
                }
            });
    }

    /**
     * Set item state.
     * @param path
     * @private
     */
    private setItemState(path: string): void {
        const menuItems: MenuItemsModel[] = this.menuConfig?.menuItems;
        if (menuItems?.length) {
            this.closeMenuOnPath(menuItems, path);
            this.clearAllItemsState(menuItems, path);
            this.setActiveItems(path, menuItems);
        } else {
            this.logService.debug('Menu30Component:setItemState', 'Unable to find menu items.');
        }
    }

    /**
     * Close menu if path has children.
     * @param menuItems
     * @param path
     * @private
     */
    private closeMenuOnPath(menuItems: MenuItemsModel[], path: string): void {
        const pathParts: string[] = path.split('/');
        const parent: string = pathParts[pathParts.length - 2];
        const parentMenuItem: MenuItemsModel = menuItems.find((item: MenuItemsModel): boolean => item.location === './' + parent);
        if (pathParts.includes('cm')) {
            return;
        }

        if (!parentMenuItem) {
            this.stepThroughPathParts(menuItems, pathParts);
        } else {
            if (parentMenuItem?.children?.length) {
                this.isOpen = false;
                this.menuService.menuIsOpen.next(this.isOpen);
            }
        }
    }

    /**
     * Step through path parts.
     * "closeMenuOnPath" does a negative lookup removing the last two parts of the path,
     * this function steps through the path parts from the beginning looking for a match.
     * @param menuItems
     * @param pathParts
     * @private
     */
    private stepThroughPathParts(menuItems: any, pathParts: string[]): void {
        if (pathParts[0] === '.') {
            pathParts.shift();
        }
        const path: string = pathParts[0];
        const parentMenuItem: MenuItemsModel = menuItems.find(
            (item: MenuItemsModel): boolean => item.id === path || item.id === 'menu-' + path
        );

        if (parentMenuItem?.children?.length) {
            pathParts.shift();
            this.stepThroughPathParts(parentMenuItem.children, pathParts);
        } else {
            this.isOpen = false;
            this.menuService.menuIsOpen.next(this.isOpen);
        }
    }

    /**
     * Clear all active items including children.
     * @param menuItems
     * @param path
     * @private
     */
    private clearAllItemsState(menuItems: any[], path?: string): void {
        if (menuItems?.length) {
            for (const item of menuItems) {
                if (item.active) {
                    item.active = false;
                }

                if (item.visible) {
                    item.visible = false;
                }

                if (item.children?.length) {
                    this.clearAllItemsState(item.children, path);
                }
            }
        }
    }

    /**
     * Set active items
     * @param path
     * @param menuItems
     * @private
     */
    private setActiveItems(path: string | any[], menuItems: MenuItemsModel[]): void {
        const activeIndex: number = menuItems.findIndex(
            (item: any): boolean => path.includes(item?.location) || item?.location?.includes(path)
        );

        if (activeIndex !== -1) {
            menuItems[activeIndex].active = true;
            menuItems[activeIndex].visible = true;

            if (menuItems[activeIndex].children?.length) {
                this.setActiveItems(path, menuItems[activeIndex].children);
            }
        }
    }

    /**
     * Set up level 2 menu for the view.
     * @private
     */
    private setUpLevel2MenuForView(): void {
        this.level2MenuItems = this.menuConfig?.menuItems?.filter((item: MenuItemsModel) => item.children?.length);
    }
}
